// Transcriber over a direct browser -> Deepgram WebSocket.
//
// There is no relay and no backend of ours in this path (see
// docs/no-backend.md). A Lambda mints a short-lived token, the browser opens
// the provider socket itself, and audio goes straight from the tab to Deepgram.
// The long-lived credential never leaves Secrets Manager, which is the
// constraint that mattered - "no STT credentials in client code, ever" is about
// the credential, not about owning a hop.
//
// This is the provider adapter, and the only file in the browser that knows
// what Deepgram's wire format looks like. Nothing below `Transcriber` sees it.
// Swapping providers means a new Lambda and a sibling of this file.

import { SAMPLE_RATE } from '../audio/AudioSource';
import { endpoints } from '../config/endpoints';
import type { Transcriber, TranscriptSegment } from './Transcriber';
import { debugLog, describe } from '../debug';

/**
 * What the token endpoint hands back. The socket URL and the auth scheme are
 * chosen server-side and mirrored back verbatim, so what the client connects to
 * cannot drift from what the endpoint authorized.
 */
type SttGrant = {
  token: string;
  scheme: string;
  url: string;
  model: string;
  expiresIn: number;
};

type DeepgramResults = {
  type?: string;
  is_final?: boolean;
  start?: number;
  duration?: number;
  channel?: { alternatives?: Array<{ transcript?: string }> };
  description?: string;
  error?: string;
};

const KEEPALIVE_INTERVAL_MS = 8000;

export class DeepgramTranscriber implements Transcriber {
  private socket: WebSocket | null = null;
  private keepalive: ReturnType<typeof setInterval> | null = null;
  private finalHandlers: Array<(segment: TranscriptSegment) => void> = [];
  private partialHandlers: Array<(text: string) => void> = [];
  private errorHandlers: Array<(err: Error) => void> = [];
  private bytesSent = 0;
  private messages = 0;
  private model = '';

  constructor(private readonly sampleRate: number = SAMPLE_RATE) {}

  /** Spec s11: STT seconds are the cost driver worth watching. */
  get secondsStreamed(): number {
    return this.bytesSent / (this.sampleRate * 2);
  }

  get providerModel(): string {
    return this.model;
  }

  onError(handler: (err: Error) => void): void {
    this.errorHandlers.push(handler);
  }

  private async grant(): Promise<SttGrant> {
    if (!endpoints.sttToken) {
      throw new Error('VITE_STT_TOKEN_URL is not set in this build.');
    }
    const response = await fetch(endpoints.sttToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sampleRate: this.sampleRate }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error ?? `could not mint a transcription token (${response.status})`);
    }
    return response.json();
  }

  async start(): Promise<void> {
    // The token only has to be valid for the handshake; the socket stays open
    // long after it expires. Minting it here, rather than at app load, keeps the
    // window between issue and use as short as it can be.
    const grant = await this.grant();
    this.model = grant.model;
    debugLog.info('stt: token minted, model =', grant.model, 'expiresIn =', grant.expiresIn);

    await new Promise<void>((resolve, reject) => {
      // Browsers cannot set an Authorization header on a WebSocket, so the
      // credential rides in Sec-WebSocket-Protocol. The scheme comes from the
      // endpoint ("bearer" for a minted token) rather than being hardcoded.
      const socket = new WebSocket(grant.url, [grant.scheme, grant.token]);
      socket.binaryType = 'arraybuffer';
      this.socket = socket;

      const failFast = () => reject(new Error('could not reach the transcription service'));
      socket.addEventListener('error', failFast, { once: true });

      socket.addEventListener('open', () => {
        socket.removeEventListener('error', failFast);
        socket.addEventListener('error', () =>
          this.emitError(new Error('transcription socket error')),
        );
        // A quiet stretch of call must not drop the socket.
        this.keepalive = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'KeepAlive' }));
          }
        }, KEEPALIVE_INTERVAL_MS);
        debugLog.info('stt: socket open ->', grant.url.split('?')[0]);
        resolve();
      });

      socket.addEventListener('message', (event) => {
        if (typeof event.data !== 'string') return;
        let msg: DeepgramResults;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        if (msg.type === 'Error' || msg.error) {
          debugLog.error('stt: provider error', describe(msg));
          this.emitError(new Error(msg.description ?? msg.error ?? 'transcription error'));
          return;
        }
        this.messages += 1;
        if (this.messages <= 3 || this.messages % 50 === 0) {
          debugLog.info('stt: message', this.messages, 'type =', msg.type ?? '(none)');
        }
        if (msg.type !== 'Results') return;

        const text = (msg.channel?.alternatives?.[0]?.transcript ?? '').trim();
        if (!text) return;

        debugLog.info('stt: transcript', msg.is_final ? '(final)' : '(partial)', text.slice(0, 60));
        if (msg.is_final) {
          const startMs = Math.round((msg.start ?? 0) * 1000);
          const endMs = startMs + Math.round((msg.duration ?? 0) * 1000);
          this.finalHandlers.forEach((handler) => handler({ text, startMs, endMs }));
        } else {
          // Display only. The extraction loop never sees these (spec s5.1).
          this.partialHandlers.forEach((handler) => handler(text));
        }
      });

      socket.addEventListener('close', (event) => {
        debugLog.warn('stt: socket closed', `code=${event.code}`, event.reason || '');
        this.clearKeepalive();
      });
    });
  }

  push(pcm: Int16Array): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(pcm.buffer as ArrayBuffer);
    this.bytesSent += pcm.byteLength;
  }

  onFinal(handler: (segment: TranscriptSegment) => void): void {
    this.finalHandlers.push(handler);
  }

  onPartial(handler: (text: string) => void): void {
    this.partialHandlers.push(handler);
  }

  async stop(): Promise<void> {
    const socket = this.socket;
    this.socket = null;
    this.clearKeepalive();
    if (!socket) return;

    if (socket.readyState === WebSocket.OPEN) {
      // Ask Deepgram to flush any in-flight audio, then give trailing finals a
      // moment to land before hanging up.
      socket.send(JSON.stringify({ type: 'CloseStream' }));
      await new Promise((resolve) => setTimeout(resolve, 400));
      socket.close(1000, 'session ended');
    } else {
      socket.close();
    }
  }

  private clearKeepalive(): void {
    if (this.keepalive) clearInterval(this.keepalive);
    this.keepalive = null;
  }

  private emitError(err: Error): void {
    if (this.errorHandlers.length === 0) console.error('[stt]', err);
    this.errorHandlers.forEach((handler) => handler(err));
  }
}
